import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { bdApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Bell, CheckCircle2, AlertCircle, Calendar as CalIcon,
} from "lucide-react";
import { CATEGORY_LABELS } from "./BDHub";

const KIND_COLOR = {
  opportunity_deadline: "border-l-red-500",
  meeting: "border-l-emerald-500",
  followup: "border-l-amber-500",
};
const COLOR_DOT = {
  red: "bg-red-500", amber: "bg-amber-500", blue: "bg-blue-500",
  emerald: "bg-emerald-500", orange: "bg-orange-500", purple: "bg-purple-500",
  slate: "bg-slate-400",
};

function toISO(d) { return d.toISOString().slice(0, 10); }
function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const rows = [];
  const cur = new Date(start);
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++) {
      row.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    rows.push(row);
  }
  return rows;
}
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// ═════════════════════ BD CALENDAR ═════════════════════
export function BDCalendar() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [category, setCategory] = useState("all");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const start = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0);
    const params = { start: start.toISOString(), end: end.toISOString() };
    if (category !== "all") params.category = category;
    bdApi.calendar(params).then(d => setEvents(d.events || [])).catch(() => setEvents([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor, category]);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach(e => {
      if (!e.date) return;
      const key = String(e.date).slice(0, 10);
      map[key] = map[key] || [];
      map[key].push(e);
    });
    return map;
  }, [events]);

  const generateReminders = async () => {
    try {
      const r = await bdApi.generateReminders();
      toast.success(`Generated ${r.created || 0} reminder${r.created === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const matrix = monthMatrix(cursor.getFullYear(), cursor.getMonth());
  const todayISO = toISO(today);

  return (
    <div data-testid="bd-calendar-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev-month"><ChevronLeft size={14}/></Button>
          <div className="font-serif text-lg text-slate-900 min-w-[180px] text-center" data-testid="cal-month-label">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          <Button variant="outline" size="sm" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next-month"><ChevronRight size={14}/></Button>
          <Button variant="outline" size="sm" onClick={()=>setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="cal-today">Today</Button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48" data-testid="cal-category-filter"><SelectValue placeholder="All categories"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="cod-bg-red" size="sm" onClick={generateReminders} data-testid="cal-generate-reminders"><Bell size={14} className="mr-1"/> Generate reminders</Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest font-semibold text-slate-600">
          {WEEKDAYS.map(w => <div key={w} className="px-2 py-2 text-center">{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {matrix.map((row, ri) => row.map((day, di) => {
            const iso = toISO(day);
            const inMonth = day.getMonth() === cursor.getMonth();
            const isToday = iso === todayISO;
            const dayEvents = byDay[iso] || [];
            return (
              <div key={`${ri}-${di}`} data-testid={`cal-cell-${iso}`}
                className={`min-h-[104px] border-b border-r border-slate-200 p-1.5 last:border-r-0 ${inMonth ? "bg-white" : "bg-slate-50/50"}`}>
                <div className={`text-xs font-semibold mb-1 flex items-center justify-between ${inMonth ? "text-slate-900" : "text-slate-400"}`}>
                  <span className={isToday ? "inline-flex items-center justify-center h-5 w-5 rounded-full bg-[var(--cod-red)] text-white" : ""}>{day.getDate()}</span>
                  {dayEvents.length > 0 && <span className="text-[10px] text-slate-500">{dayEvents.length}</span>}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(e => (
                    <Link key={e.id} to={e.url || "#"} data-testid={`cal-event-${e.id}`}
                      className={`block px-1.5 py-1 rounded border-l-2 ${KIND_COLOR[e.kind] || "border-l-slate-400"} bg-slate-50 hover:bg-slate-100`}>
                      <div className="text-[11px] font-medium text-slate-900 truncate flex items-center gap-1">
                        <span className={`inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${COLOR_DOT[e.color] || "bg-slate-400"}`}/>
                        <span className="truncate">{e.title}</span>
                      </div>
                      {e.organization_name && <div className="text-[10px] text-slate-500 truncate">{e.organization_name}</div>}
                    </Link>
                  ))}
                  {dayEvents.length > 3 && <div className="text-[10px] text-slate-500">+{dayEvents.length - 3} more</div>}
                </div>
              </div>
            );
          }))}
        </div>
      </div>
      {loading && <div className="text-xs text-slate-500 mt-2">Loading events…</div>}
    </div>
  );
}

// ═════════════════════ BD TASKS/FOLLOW-UPS ═════════════════════
export function BDTasks() {
  const [scope, setScope] = useState("all");
  const [tasks, setTasks] = useState([]);
  const load = () => bdApi.tasks(scope).then(setTasks).catch(()=>setTasks([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);
  const complete = async (aid) => {
    try { await bdApi.completeTask(aid); toast.success("Marked complete"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="bd-tasks-view">
      <div className="flex gap-2 mb-4" data-testid="bd-tasks-chips">
        {[["all","All"],["mine","My follow-ups"],["overdue","Overdue"],["due_soon","Due next 7d"]].map(([v, l]) => (
          <button key={v} onClick={()=>setScope(v)} data-testid={`bd-tasks-chip-${v}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${scope === v ? "bg-[var(--cod-navy)] text-white border-[var(--cod-navy)]" : "bg-white text-slate-700 border-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="space-y-2" data-testid="bd-tasks-list">
        {tasks.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No open follow-ups.</div>
        )}
        {tasks.map(t => (
          <div key={t.id} className={`rounded-lg border ${t.is_overdue ? "border-red-200 bg-red-50/30" : "border-slate-200 bg-white"} p-3 flex items-center justify-between gap-3`} data-testid={`bd-task-${t.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {t.is_overdue && <AlertCircle size={14} className="text-red-600 flex-shrink-0"/>}
                <div className="text-sm font-semibold text-slate-900 truncate">{t.subject}</div>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t.organization_id ? (<Link to={`/staff/bd/organizations/${t.organization_id}`} className="cod-link">{t.organization_name || "—"}</Link>) : "—"}
                {t.due_at && <> · Due {String(t.due_at).slice(0, 10)}</>}
              </div>
              {t.body && <div className="text-xs text-slate-600 mt-1 line-clamp-1">{t.body}</div>}
            </div>
            <Button size="sm" variant="outline" onClick={()=>complete(t.id)} data-testid={`bd-task-complete-${t.id}`}><CheckCircle2 size={14} className="mr-1"/>Complete</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
